import { Chip, Typography } from "@mui/material";
import { useState } from "react";
import { QuestionInfo } from "../../../assets/common-svg/CommonSvg";
import CustomDrawer from "../../../components/custom-drawer/CustomDrawer";
import HeaderBackNav from "../../../components/header-back-navigation/HeaderBackNav";
import { useDispatch } from "react-redux";
import { fetchQueryHistoryForCoa } from "../../../bmr/slices/queryPreScreen";
import CustomAccordion from "../../../components/custom-accordion/CustomAccordion";
import { FormValues } from "../../../components/form-template/FormValues";
import { FormCommentList } from "../../../components/form-template/FormCommentList";
import NoDataCard from "../../../components/no-data-card";
import { notify } from "../../utils/methods/notification";

const CoaQueryHistory = ({ preScreenId, queriesFields }) => {
  const dispatch = useDispatch();
  const [queryHistory, setQueryHistory] = useState([]);
  const [historyDrawer, setHistoryDrawer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleHistoryToggle = async () => {
    if (queriesFields.length === 0 && !isLoading) {
      notify.error("There are no queries raised for this form")
    } else {
      getHistory()
      setHistoryDrawer(true);
    }
  };

  const getHistory = async () => {
    if (!preScreenId) return;

    setIsLoading(true);
    try {
      const res = await dispatch(fetchQueryHistoryForCoa(preScreenId));

      if (res?.payload?.status === 200) {
        const data = res?.payload?.data.map((op) => ({
          title: op.field_label || op.field_name,
          leftBorder: false,
          backgroundColor: "#F0F1F5",
          color: "#000000",
          expanded: false,
          status: op.status,
          field_name: op.field_name,
          field_id: op.field_id,
          created_at: op.created_at,
          updated_at: op.updated_at,
          menuData: op.queries_comment.map((comment) => ({
            title: `${comment.raised_by} (${comment.role})`,
            name: `${comment.raised_by[0]}${comment.raised_by[1]}`,
            describe: comment.query,
            date: comment.created_at,
            role: comment.role,
            user_id: comment.user_id,
          })),
          values: {
            previous_value: op.previous_value,
            new_value: op.new_value,
          },
        }));
        setQueryHistory(data);
      } else {
        setQueryHistory([]);
      }
    } catch (error) {
      console.error("Error fetching COA query history:", error);
      setQueryHistory([]);
      notify.error("Failed to load query history")
    } finally {
      setIsLoading(false);
    }
  };


  const toggleDrawer = () => {
    setHistoryDrawer(false);
  };

  const handleQuery = (index) => {
    const updatedHistory = [...queryHistory];
    if (updatedHistory[index] && updatedHistory[index].expanded !== undefined) {
      updatedHistory[index].expanded = !updatedHistory[index].expanded;
    }
    setQueryHistory(updatedHistory);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 0:
        return "warning";
      case 1:
        return "success";
      case 2:
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 0:
        return "Pending";
      case 1:
        return "Resolved";
      case 2:
        return "Rejected";
      default:
        return "Unknown";
    }
  };

  return (
    <div>
      <div className="flex flex-row-reverse mt-2 mb-3">
        <div className="flex gap-2 items-center">
          <Typography variant="h6" className="font-medium">
            Query History
          </Typography>
          <span
            className="cursor-pointer hover:opacity-70 transition-opacity"
            onClick={() => handleHistoryToggle()}
            title="View query history"
          >
            <QuestionInfo />
          </span>
        </div>
      </div>

      <CustomDrawer open={historyDrawer} toggleDrawer={toggleDrawer}>
        <div className="pl-4 pt-4 pr-4">
          <HeaderBackNav
            handleBackNav={toggleDrawer}
            TitleFontSize="18px"
            TitleFontWeight={700}
            Title="COA Query History"
          />
        </div>
        <hr />

        <div className="pl-4 pt-4 pr-4 pb-4">
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <Typography variant="body2" color="text.secondary">
                Loading query history...
              </Typography>
            </div>
          ) : queryHistory?.length !== 0 ? (
            <div className="space-y-3">
              {queryHistory.map((query, index) => (
                <CustomAccordion
                  key={`query-${index}-${query.field_id}`}
                  accordionClassName="w-[25rem]"
                  rootDivClass="pb-3"
                  index={index}
                  chip={
                    <Chip
                      className="mt-3 mr-2"
                      label={getStatusLabel(query.status)}
                      color={getStatusColor(query.status)}
                      size="small"
                    />
                  }
                  expanded={query?.expanded}
                  handleExpansion={handleQuery}
                  color={query?.color}
                  backgroundColor={query?.backgroundColor}
                  title={query?.title}
                >
                  {/* Value Changes */}
                  {query.status === 2 ? (
                    <div className="w-100 text-center mb-3">
                      <div className="text-zinc-500 text-sm font-normal font-['Public Sans'] pb-1">
                        Current Value
                      </div>
                      <div className="px-2 py-1 bg-orange-400 rounded justify-center items-start gap-2.5 inline-flex">
                        <div className="text-white text-sm font-normal font-['Public Sans'] break-all">
                          {query?.values?.previous_value || "No value"}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <FormValues
                      leftLable="Previous Value"
                      leftValue={query?.values?.previous_value || "No value"}
                      rightLable="New Value"
                      rightValue={query?.values?.new_value || "N/A"}
                    />
                  )}

                  <hr className="pb-3" />

                  {/* Comments/Queries */}
                  <div>
                    {query.menuData && query.menuData.length > 0 ? (
                      <FormCommentList MenuData={query.menuData} />
                    ) : (
                      <Typography variant="body2" color="text.secondary" className="text-center py-2">
                        No comments available
                      </Typography>
                    )}
                  </div>
                </CustomAccordion>
              ))}
            </div>
          ) : (
            <div className="mt-4">
              <NoDataCard
                title="No Query History"
                description="No queries have been raised for this COA form yet."
              />
            </div>
          )}
        </div>
      </CustomDrawer>
    </div>
  );
};

export default CoaQueryHistory;